import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link } from "react-router-dom";

import FormInput from "@ats/src/components/forms/FormInput";
import SlidingToggleSwitch, { OptionPair } from "@ats/src/components/forms/SlidingToggleSwitch";
import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import AdminOrganizationEditModal from "@ats/src/views/admin/AdminOrganizationEditModal";
import AdminRemoveBadActorModal from "@ats/src/views/admin/AdminRemoveBadActorModal";
import AdminCareersPageSubscriptionsModal from "@ats/src/views/admin/AdminCareersPageSubscriptionsModal";

import { useOrganizations, useSearchOrganizations } from "@shared/queryHooks/admin/useOrganization";
import { useBecomeUser } from "@shared/queryHooks/admin/useBecome";

import { getOptionLabel, adminOrganizationFlipperGroupOptions } from "@ats/src/lib/lookups";

import Button from "@ats/src/components/shared/Button";
import { useModalContext } from "@shared/context/ModalContext";
import { capitalizeSingleWord } from "../../lib/utils/helpers";

const subscriptionStatusOptions: OptionPair = [
  { label: "Active", value: "active" },
  { label: "Canceled", value: "canceled" },
];

function AdminDashboardCustomers(props: any) {
  const { openModal, removeModal } = useModalContext();

  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = React.useState("active");
  const [sortBy, setSortBy] = React.useState("created_at_desc");

  const filters = React.useMemo(() => {
    const baseFilters = {
      is_claimed: true,
      order_by: sortBy,
      customers: true,
    };

    if (subscriptionStatus === "active") {
      return {
        ...baseFilters,
        stripe_subscription_status_active: true,
      };
    } else {
      return {
        ...baseFilters,
        with_canceled_subscription: true,
      };
    }
  }, [subscriptionStatus, sortBy]);

  const {
    data: customerOrganizations,
    isFetching: isFetchingCustomers,
    isSuccess: isSuccessCustomers,
  } = useOrganizations({ filters });

  React.useEffect(() => {
    window.logger("%c[AdminDashboardCustomers] Filters changed", "color: orange", { filters });
  }, [filters]);
  const { mutate: becomeUser, isLoading: isLoadingBecome } = useBecomeUser();

  const {
    mutate: searchOrganizations,
    isSuccess: isSuccessSearchOrganizations,
  } = useSearchOrganizations();

  window.logger("%c[AdminDashboardCustomers] render", "color: #1976D2", {
    props,
    customerOrganizations,
    isFetchingCustomers,
  });

  const handleBecomeUser = (userId) => {
    window.logger("%c[AdminDashboardCustomers] handleBecomeUser userId", "color: #1976D2", userId);

    becomeUser(
      { userId },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[AdminDashboardCustomers] becomeUser onSuccess",
            "background-color: #c6d219",
            { data },
          );
          window.location.href = `${window.APP_ATS_ROOT_URL}/jobs`;
        },
        onError: (response: any) => {
          window.logger("[AdminDashboardCustomers] becomeUser server error", { response });
          if (response.data.errors) {
            // setState({ ...state, errors: response.data.errors });
          }
        },
      },
    );
  };

  const onChangeSearchOrganizations = (_, value) => {
    setSearchTerm(value);
    if (value.length > 0) {
      searchOrganizations(value, {
        onSuccess: (data) => {
          window.logger(
            "%c[AdminDashboardCustomers] onChangeSearchOrganizations",
            "color: #1976D2",
            { data },
          );
          setSearchResults(data.items);
        },
      });
    } else {
      setSearchResults([]);
    }
  };

  const handleSubscriptionStatusChange = (_, value) => {
    setSubscriptionStatus(value);
    setSearchResults([]);
    setSearchTerm("");
  };

  const handleOnClickEditOrganization = (organization) => {
    const modal = <AdminOrganizationEditModal organization={organization} onCancel={removeModal} />;

    openModal(modal);
  };

  const handleOnClickTakeover = (organization) => {
    const modal = (
      <AdminRemoveBadActorModal
        organizationId={organization.id}
        organizationName={organization.name}
        onCancel={removeModal}
      />
    );
    openModal(modal);
  };

  const handleOnClickUpdateSubscriptions = (organization) => {
    const modal = (
      <AdminCareersPageSubscriptionsModal organization={organization} onCancel={removeModal} />
    );
    openModal(modal);
  };

  const organizationNodes = () => {
    if (!isSuccessCustomers || customerOrganizations == undefined) {
      return [];
    }

    const organizations =
      searchResults.length > 0 && searchTerm.length > 0
        ? searchResults
        : customerOrganizations.items;

    window.logger("%c[AdminDashboardCustomers] organizationNodes", "color: #1976D2", {
      organizations,
    });

    return organizations.map(function (organization) {
      let disableBecome = organization?.owner?.email === "system@inflowhq.com";

      return (
        <tr className="admin-organization-list__item" key={organization.id}>
          <td>{organization.id}</td>
          <td>{organization.name}</td>
          {/* <td>{organization.twitterHandle ? <Icon name="twitter" /> : ""}</td> */}
          <td>
            <Link to={`/admin/jobs?organization_id=${organization.id}`}>
              {organization.publishedJobsCount} Job(s)
            </Link>
          </td>
          <td>
            <div>{organization?.owner?.fullName}</div>
            {organization?.owner?.email}
          </td>
          <td>{getOptionLabel(adminOrganizationFlipperGroupOptions, organization.flipperGroup)}</td>
          <td>
            {organization.careersPageUrl != undefined ? (
              <a href={organization.careersPageUrl} target="_blank" rel="noopener noreferrer">
                {organization?.careersPageSlug}
              </a>
            ) : null}
          </td>
          <td
            style={{ color: `${organization.stripeSubscriptionInGoodStanding ? "green" : "red"}` }}
          >
            {organization.stripeSubscriptionInGoodStanding ? "Active" : "Inactive"}
          </td>
          <td>
            <Styled.Tag>{capitalizeSingleWord(organization?.fraudRating)}</Styled.Tag>
          </td>
          <td>
            <Button
              styleType="text"
              type="button"
              onClick={() => handleBecomeUser(organization.owner.id)}
              disabled={disableBecome}
            >
              Become
            </Button>
          </td>
          <td>
            <DropdownMenu icon="settings" label="Organization actions" testid="organization-actions-menu">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  props.history.push(`/admin/org-jobs?organizationId=${organization.id}`);
                }}
              >
                View Jobs
              </button>
              <button onClick={() => handleOnClickEditOrganization(organization)}>
                Edit organization
              </button>
              <button onClick={() => handleOnClickUpdateSubscriptions(organization)}>
                Update careers page subscriptions
              </button>
              <button onClick={() => handleOnClickTakeover(organization)}>
                Takeover & Shutdown
              </button>
            </DropdownMenu>
          </td>
        </tr>
      );
    });
  };

  const orgCount = organizationNodes().length;

  return (
    <Styled.Component>
      <Styled.Header>
        <h2>
          {orgCount} Most Recent {subscriptionStatus === "active" ? "PAID" : "CHURNED"} Customers{" "}
          <span>{false && orgCount}</span>
        </h2>

        <Styled.HeaderControls>
          <Styled.ControlItem>
            <SlidingToggleSwitch
              name="subscriptionStatus"
              value={subscriptionStatus}
              options={subscriptionStatusOptions}
              onChange={handleSubscriptionStatusChange}
            />
          </Styled.ControlItem>
          {subscriptionStatus === "canceled" && (
            <Styled.ControlItem>
              <DropdownMenu icon="arrow-down" label="Sort by">
                <button onClick={() => setSortBy("created_at_desc")}>
                  {sortBy === "created_at_desc" && "✓ "}Created Date
                </button>
                <button onClick={() => setSortBy("canceled_at_desc")}>
                  {sortBy === "canceled_at_desc" && "✓ "}Canceled Date
                </button>
              </DropdownMenu>
            </Styled.ControlItem>
          )}
          <FormInput
            name="search"
            placeholder="Search"
            value={searchTerm}
            onChange={onChangeSearchOrganizations}
          />
        </Styled.HeaderControls>
      </Styled.Header>

      <Styled.Table>
        <tbody>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Jobs</th>
            {/* <th>Users</th> */}
            <th>Owner</th>
            <th>Flipper Group</th>
            <th>Job Board</th>
            <th>Subscription</th>
            <th>Fraud Rating</th>
            <th />
            <th />
          </tr>
          {organizationNodes()}
        </tbody>
      </Styled.Table>
    </Styled.Component>
  );
}

AdminDashboardCustomers.propTypes = {};

AdminDashboardCustomers.defaultProps = {};

export default AdminDashboardCustomers;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Component = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers;
    ${t.p(4)};
  `;
});

Styled.Header = styled.header((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers_Header;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h2 {
      ${[t.mt(1), t.text.h3, t.mb(2)]};
      display: flex;
      align-items: center;
    }
    span {
      ${[t.ml(2), t.text.sm, t.text.secondary, t.text.normal]};
    }
  `;
});

Styled.HeaderControls = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers_HeaderControls;
    display: flex;
    align-items: flex-end;
    gap: ${t.spacing[4]};
  `;
});

Styled.ControlItem = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers_ControlItem;
    ${t.mb(5)};
  `;
});

Styled.Filters = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers_Filters;
    ${t.mb(6)};
    display: flex;

    button {
      ${t.mr(3)};
    }
  `;
});

Styled.Table = styled.table((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardCustomers_Table;
    border-collapse: collapse;
    width: 100%;

    th {
      ${[t.pb(2), t.text.xs, t.text.bold]};
      text-align: start;
      ${t.text.primary};
    }

    tr {
      border-bottom: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      ${t.text.secondary};
    }

    tr:last-of-type {
      border-bottom: none;
    }

    svg {
      stroke: ${t.color.black};
    }

    td,
    th {
      ${[t.p(2)]};

      &:first-of-type {
        ${t.pl(0)}
      }

      &:last-of-type {
        ${t.pr(0)}
      }
    }
  `;
});

Styled.Tag = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationSidebar_Tag;
    ${[t.h(6), t.px(2), t.text.xs, t.rounded.xs]}
    display: inline-flex;
    align-items: center;
    color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    line-height: 1.4;
    overflow: hidden;
    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  `;
});
